// src/components/ui/tabs.js
import React, { useState, createContext, useContext, useEffect } from 'react';

const TabsContext = createContext(null);

export const Tabs = ({ children, defaultValue, value, onChange, className = '', ...props }) => {
  // Internal state for uncontrolled component
  const [selectedTab, setSelectedTab] = useState(defaultValue);
  
  // If this is a controlled component, use the value prop
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : selectedTab;
  
  // Update internal state if controlled value changes
  useEffect(() => {
    if (isControlled && value !== selectedTab) {
      setSelectedTab(value);
    }
  }, [isControlled, value, selectedTab]);
  
  const handleTabChange = (newValue) => {
    console.log('[Tabs] Tab change:', newValue);
    
    // Always update internal state
    if (!isControlled) {
      setSelectedTab(newValue);
    }
    
    // Call onChange handler if provided
    if (onChange) {
      onChange(newValue);
    }
  };
  
  return (
    <TabsContext.Provider value={{ value: currentValue, onChange: handleTabChange }}>
      <div className={`tabs-container ${className}`} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

export const TabsList = ({ children, className = '', ...props }) => {
  return (
    <div 
      className={`flex space-x-1 mb-4 border-b border-gray-200 ${className}`} 
      role="tablist"
      {...props}
    >
      {children}
    </div>
  );
};

export const TabsTrigger = ({ children, value, className = '', ...props }) => {
  const { value: selectedValue, onChange } = useContext(TabsContext);
  const isActive = selectedValue === value;
  
  return (
    <button
      role="tab"
      aria-selected={isActive}
      className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors
        ${isActive 
          ? 'text-blue-600 bg-white border-blue-600 border-b-2' 
          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'}
        ${className}`}
      onClick={() => onChange(value)}
      {...props}
    >
      {children}
    </button>
  );
};
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = ({ children, value, className = '', ...props }) => {
  const { value: selectedValue } = useContext(TabsContext);
  const isSelected = selectedValue === value;
  
  if (!isSelected) return null;
  
  return (
    <div 
      role="tabpanel"
      className={`py-4 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};
TabsContent.displayName = "TabsContent";